import React from "react";
import { Group, Loader, Progress, Text, Tooltip } from "@mantine/core";
import { Badge } from "@xplatform/design-system";
import { IconCheck, IconAlertTriangle } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useMappingJob } from "@hooks/extraction/useMappingJob";
import { MappingViewStatus } from "@models/mapping";
import "./MappingStatusBadge.scss";

interface MappingStatusBadgeProps {
  processId: string;
  enabled?: boolean;
}

/**
 * Inline badge + progress bar showing process-wide mapping status alongside
 * an extraction result item. Renders nothing until a job exists.
 */
const MappingStatusBadge: React.FC<MappingStatusBadgeProps> = ({
  processId,
  enabled = true,
}) => {
  const { t } = useTranslation();
  const { job } = useMappingJob(processId, enabled);

  if (!job) {
    return null;
  }

  if (job.status === MappingViewStatus.Completed && job.failedFiles > 0) {
    return (
      <Badge
        className="mapping-status-badge"
        color="orange"
        variant="light"
        size="sm"
        leftSection={<IconAlertTriangle size={12} />}
      >
        {t("extraction.mappingStatus.partial", { count: job.failedFiles })}
      </Badge>
    );
  }

  if (job.status === MappingViewStatus.Completed) {
    return (
      <Badge
        className="mapping-status-badge"
        color="teal"
        variant="light"
        size="sm"
        leftSection={<IconCheck size={12} />}
      >
        {t("extraction.mappingStatus.completed")}
      </Badge>
    );
  }

  if (job.status === MappingViewStatus.Failed) {
    return (
      <Badge
        className="mapping-status-badge"
        color="red"
        variant="light"
        size="sm"
        leftSection={<IconAlertTriangle size={12} />}
      >
        {t("extraction.mappingStatus.failed")}
      </Badge>
    );
  }

  // Running (AwaitingUpload / Parsing / Running).
  const hasTotal = job.rowsTotal > 0;
  const percent = hasTotal
    ? Math.min(100, Math.round((job.rowsDone / job.rowsTotal) * 100))
    : 0;
  const progressLabel = hasTotal
    ? t("extraction.mappingStatus.rows", {
        done: job.rowsDone,
        total: job.rowsTotal,
      })
    : t("extraction.mappingStatus.preparing");

  return (
    <Tooltip label={progressLabel} position="top" withArrow>
      <div className="mapping-status-progress">
        <Group gap={4} wrap="nowrap" justify="center">
          <Loader size={10} />
          <Text size="xs" className="mapping-status-label">
            {t("extraction.mappingStatus.running")}
          </Text>
          {hasTotal && (
            <Text size="xs" c="dimmed">
              {`${job.rowsDone}/${job.rowsTotal}`}
            </Text>
          )}
        </Group>
        {hasTotal && (
          <Progress
            value={percent}
            size="xs"
            color="blue"
            animated
            className="mapping-status-bar"
          />
        )}
      </div>
    </Tooltip>
  );
};

export default MappingStatusBadge;
